import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  User,
  Mail,
  Phone,
  Building,
  Globe,
  Briefcase,
  Users,
  MessageSquare,
  Play,
  Loader2,
  Calendar,
  Edit,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  addNote,
  getNotes,
  convertLeadToTrial,
  updateLead,
} from "../../apiHelper";
import NoteItem from "./NoteItem";

const LeadItem = (props) => {
  const { lead, onLeadUpdate, salespeople = [] } = props;
  const { getAccessTokenSilently } = useAuth();
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteValue, setNoteValue] = useState("");
  const [provisioningTrial, setProvisioningTrial] = useState(false);
  const [trialData, setTrialData] = useState(null); // Store trial data locally
  const [errorMessage, setErrorMessage] = useState(null);
  const [myNotes, setMyNotes] = useState({});
  const [trialButtonDisabled, setTrialButtonDisabled] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Helper function to get salesperson name by ID
  const getSalespersonName = (salespersonId) => {
    if (!salespersonId || salespersonId === "unassigned") return "Not assigned";
    const salesperson = salespeople.find((sp) => sp._id === salespersonId);
    return salesperson ? salesperson.name : "Unknown";
  };
  const [isEditingLeadManagement, setIsEditingLeadManagement] = useState(false);
  const [editData, setEditData] = useState({});
  const [leadManagementData, setLeadManagementData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingLeadManagement, setIsSavingLeadManagement] = useState(false);

  useEffect(() => {
    setTrialButtonDisabled(lead.accountType === "trial" && lead.tenantId);

    setEditData({
      companySize: lead.companySize || "",
      industry: lead.industry || "",
      jobTitle: lead.jobTitle || "",
      website: lead.website || "",
      howDidYouHear: lead.howDidYouHear || "",
      specificNeeds: lead.specificNeeds || "",
    });
    setLeadManagementData({
      lastCalled: lead.lastCalled
        ? new Date(lead.lastCalled).toISOString().split("T")[0]
        : "",
      scheduledNextCall: lead.scheduledNextCall
        ? new Date(lead.scheduledNextCall).toISOString().split("T")[0]
        : "",
      leadStatus: lead.leadStatus || "cold",
      salesperson: lead.salesperson || "unassigned",
    });
  }, [lead]);

  const handleAddNote = async () => {
    getAccessTokenSilently().then((t) => {
      addNote(t, {
        leadId: lead._id,
        note: noteValue,
      })
        .then(() => {
          setNoteModalOpen(false);
          getNotes(t, {
            lead: lead._id,
          })
            .then((n) => {
              setMyNotes(n);
            })
            .catch((e) => {
              console.log(e);
            });
        })
        .catch((e) => {
          setErrorMessage(JSON.parse(e.response?.request?.response).message);
          setTimeout(() => setErrorMessage(null), 4000);
        });
    });
  };

  const handleProvisionTrial = async () => {
    try {
      setProvisioningTrial(true);

      const token = await getAccessTokenSilently();

      const response = await convertLeadToTrial(token, {
        leadId: lead._id,
        trialDurationDays: 14,
        notes: "Trial created from lead management system",
      });

      // Store trial data locally immediately so we can show the View Trial button
      // without waiting for prop updates
      setTrialData({
        tenantId: response.lead.tenantId,
        accountType: response.lead.accountType,
      });

      // Also update parent component
      if (onLeadUpdate) {
        onLeadUpdate({
          ...lead,
          tenantId: response.lead.tenantId,
          accountType: response.lead.accountType,
          leadStatus: response.lead.leadStatus,
          convertedAt: response.lead.convertedAt,
        });
      }

      // Hide loading state now that we have trial data
      setProvisioningTrial(false);
      setTrialButtonDisabled(true);

      // Refresh notes to show the trial creation
      try {
        const notes = await getNotes(token, {
          lead: lead._id,
        });
        setMyNotes(notes);
      } catch (e) {
        console.log("Error loading notes:", e);
      }
    } catch (error) {
      console.log("Error provisioning trial:", error);
      setProvisioningTrial(false);
      setErrorMessage("Failed to create trial. Please try again.");
      setTimeout(() => setErrorMessage(null), 4000);
    }
  };

  const handleExpand = async (value) => {
    console.log("Accordion value changed:", value);
    if (value === "item-1") {
      console.log("Loading notes for lead:", lead._id);
      getAccessTokenSilently().then((t) => {
        getNotes(t, {
          lead: lead._id,
        })
          .then((n) => {
            console.log("Notes loaded:", n);
            setMyNotes(n);
          })
          .catch((e) => {
            console.error("Error loading notes:", e);
          });
      });
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    getAccessTokenSilently().then((t) => {
      updateLead(t, lead._id, editData)
        .then((updatedLead) => {
          setIsSaving(false);
          setIsEditing(false);
          if (onLeadUpdate) {
            onLeadUpdate(updatedLead);
          }
        })
        .catch((e) => {
          setIsSaving(false);
          setErrorMessage("Failed to update lead. Please try again.");
          setTimeout(() => setErrorMessage(null), 4000);
        });
    });
  };

  const handleSaveLeadManagement = async () => {
    setIsSavingLeadManagement(true);
    getAccessTokenSilently().then((t) => {
      // Convert "unassigned" back to empty string for saving
      const dataToSave = {
        ...leadManagementData,
        salesperson:
          leadManagementData.salesperson === "unassigned"
            ? ""
            : leadManagementData.salesperson,
      };
      updateLead(t, lead._id, dataToSave)
        .then((updatedLead) => {
          setIsSavingLeadManagement(false);
          setIsEditingLeadManagement(false);
          if (onLeadUpdate) {
            onLeadUpdate(updatedLead);
          }
        })
        .catch((e) => {
          setIsSavingLeadManagement(false);
          setErrorMessage(
            "Failed to update lead management. Please try again."
          );
          setTimeout(() => setErrorMessage(null), 4000);
        });
    });
  };

  return (
    <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl shadow-gray-200/50 rounded-2xl overflow-hidden">
      <CardContent className="p-4 md:p-6">
        <Accordion
          type="single"
          collapsible
          className="w-full"
          onValueChange={handleExpand}
        >
          <AccordionItem value="item-1" className="border-0">
            <AccordionTrigger className="hover:no-underline py-0">
              <div className="flex-1 text-left w-full">
                {/* Desktop Layout */}
                <div className="hidden md:flex items-center justify-between w-full">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/25">
                      <User className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">
                        {lead.leadName}
                      </h3>
                      <div className="flex items-center gap-4 mt-1">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Mail className="h-4 w-4" />
                          <span>{lead.emailAddress}</span>
                        </div>
                        {lead.phoneNumber && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone className="h-4 w-4" />
                            <span>{lead.phoneNumber}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className="!bg-blue-50 !text-blue-700 !border-blue-200 px-3 py-1 rounded-lg">
                      {lead.channel || "website"}
                    </Badge>
                    {lead.accountType === "trial" && (
                      <Badge className="!bg-green-100 !text-green-800 !border-green-200 px-3 py-1 rounded-lg">
                        Trial Active
                      </Badge>
                    )}
                    {lead.salesperson && (
                      <Badge className="!bg-purple-50 !text-purple-700 !border-purple-200 px-3 py-1 rounded-lg">
                        {getSalespersonName(lead.salesperson)}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Mobile Layout */}
                <div className="md:hidden w-full">
                  <div className="flex items-start space-x-3 mb-3">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/25 flex-shrink-0">
                      <User className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-gray-900 truncate">
                        {lead.leadName}
                      </h3>
                    </div>
                  </div>

                  <div className="space-y-2 mb-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Mail className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{lead.emailAddress}</span>
                    </div>
                    {lead.phoneNumber && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{lead.phoneNumber}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Badge className="!bg-blue-50 !text-blue-700 !border-blue-200 px-2 py-1 rounded-md text-xs">
                      {lead.channel || "website"}
                    </Badge>
                    {lead.accountType === "trial" && (
                      <Badge className="!bg-green-100 !text-green-800 !border-green-200 px-2 py-1 rounded-md text-xs">
                        Trial Active
                      </Badge>
                    )}
                    {myNotes.docs && myNotes.docs.length > 0 && (
                      <Badge className="!bg-purple-50 !text-purple-700 !border-purple-200 px-2 py-1 rounded-md text-xs">
                        {myNotes.docs.length} notes
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 md:pt-6 space-y-4 md:space-y-6">
              {errorMessage && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-200">
                  <p className="text-red-800 text-sm">{errorMessage}</p>
                </div>
              )}

              {/* Lead Management Details */}
              <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-lg shadow-gray-200/30">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-3 md:mb-4 gap-3">
                  <h4 className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-2 md:gap-3">
                    <div className="w-6 h-6 md:w-8 md:h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg md:rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25">
                      <Users className="h-3 w-3 md:h-4 md:w-4 text-white" />
                    </div>
                    Lead Management
                  </h4>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setIsEditingLeadManagement(!isEditingLeadManagement)
                      }
                      className="px-4 py-2 rounded-xl border-gray-200 hover:bg-gray-50 text-sm"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      {isEditingLeadManagement ? "Cancel" : "Edit"}
                    </Button>
                  </div>
                </div>

                {isEditingLeadManagement ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Last Called
                      </label>
                      <Input
                        type="date"
                        value={leadManagementData.lastCalled}
                        onChange={(e) =>
                          setLeadManagementData({
                            ...leadManagementData,
                            lastCalled: e.target.value,
                          })
                        }
                        className="rounded-xl border-gray-200 focus:border-purple-300 focus:ring-purple-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Next Call Scheduled
                      </label>
                      <Input
                        type="date"
                        value={leadManagementData.scheduledNextCall}
                        onChange={(e) =>
                          setLeadManagementData({
                            ...leadManagementData,
                            scheduledNextCall: e.target.value,
                          })
                        }
                        className="rounded-xl border-gray-200 focus:border-purple-300 focus:ring-purple-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Lead Status
                      </label>
                      <select
                        value={leadManagementData.leadStatus}
                        onChange={(e) =>
                          setLeadManagementData({
                            ...leadManagementData,
                            leadStatus: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-purple-300 focus:ring-purple-200 text-sm"
                      >
                        <option value="cold">Cold</option>
                        <option value="warm">Warm</option>
                        <option value="dead">Dead</option>
                        <option value="demo done">Demo Done</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Salesperson
                      </label>
                      <Select
                        value={leadManagementData.salesperson}
                        onValueChange={(value) =>
                          setLeadManagementData({
                            ...leadManagementData,
                            salesperson: value,
                          })
                        }
                      >
                        <SelectTrigger className="rounded-xl border-gray-200 focus:border-purple-300 focus:ring-purple-200">
                          <SelectValue placeholder="Select salesperson" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {salespeople.map((salesperson) => (
                            <SelectItem
                              key={salesperson._id}
                              value={salesperson._id}
                            >
                              {salesperson.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Wants Demo
                      </label>
                      <div className="px-3 py-2 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-600">
                        {lead.wantsDemo ? "Yes" : "No"}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Created Date
                      </label>
                      <div className="px-3 py-2 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-600">
                        {lead.createdAt
                          ? new Date(lead.createdAt).toLocaleDateString()
                          : "Not set"}
                      </div>
                    </div>
                    <div className="col-span-1 md:col-span-2 lg:col-span-3 w-full flex justify-end gap-2 sm:gap-3 mt-4">
                      <Button
                        variant="outline"
                        onClick={() => setIsEditingLeadManagement(false)}
                        className="px-4 py-2 rounded-xl border-gray-200 hover:bg-gray-50 text-sm"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSaveLeadManagement}
                        disabled={isSavingLeadManagement}
                        className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all duration-300 text-sm"
                      >
                        {isSavingLeadManagement ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          "Save Changes"
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-gray-100 mt-1">
                        <Calendar className="h-4 w-4 text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700">
                          Last Called
                        </p>
                        <p className="text-sm text-gray-600">
                          {lead.lastCalled
                            ? new Date(lead.lastCalled).toLocaleDateString()
                            : "Not set"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-gray-100 mt-1">
                        <Calendar className="h-4 w-4 text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700">
                          Next Call Scheduled
                        </p>
                        <p className="text-sm text-gray-600">
                          {lead.scheduledNextCall
                            ? new Date(
                                lead.scheduledNextCall
                              ).toLocaleDateString()
                            : "Not set"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-gray-100 mt-1">
                        <Users className="h-4 w-4 text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700">
                          Lead Status
                        </p>
                        <p className="text-sm text-gray-600">
                          {lead.leadStatus || "cold"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-gray-100 mt-1">
                        <User className="h-4 w-4 text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700">
                          Salesperson
                        </p>
                        <p className="text-sm text-gray-600">
                          {getSalespersonName(lead.salesperson)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-gray-100 mt-1">
                        <Play className="h-4 w-4 text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700">
                          Wants Demo
                        </p>
                        <p className="text-sm text-gray-600">
                          {lead.wantsDemo ? "Yes" : "No"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-gray-100 mt-1">
                        <Calendar className="h-4 w-4 text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700">
                          Created Date
                        </p>
                        <p className="text-sm text-gray-600">
                          {lead.createdAt
                            ? new Date(lead.createdAt).toLocaleDateString()
                            : "Not set"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Company Details */}
              {(lead.companySize ||
                lead.industry ||
                lead.jobTitle ||
                lead.website ||
                lead.howDidYouHear ||
                lead.specificNeeds) && (
                <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-lg shadow-gray-200/30">
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-3 md:mb-4 gap-3">
                    <h4 className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-2 md:gap-3">
                      <div className="w-6 h-6 md:w-8 md:h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg md:rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                        <Building className="h-3 w-3 md:h-4 md:w-4 text-white" />
                      </div>
                      Company Details
                    </h4>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditing(!isEditing)}
                        className="px-4 py-2 rounded-xl border-gray-200 hover:bg-gray-50 text-sm"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        {isEditing ? "Cancel" : "Edit"}
                      </Button>
                    </div>
                  </div>

                  {isEditing ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Company Size
                        </label>
                        <Input
                          value={editData.companySize}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              companySize: e.target.value,
                            })
                          }
                          placeholder="e.g., 11-50"
                          className="rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Industry
                        </label>
                        <Input
                          value={editData.industry}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              industry: e.target.value,
                            })
                          }
                          placeholder="e.g., Healthcare"
                          className="rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Job Title
                        </label>
                        <Input
                          value={editData.jobTitle}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              jobTitle: e.target.value,
                            })
                          }
                          placeholder="e.g., HR Manager"
                          className="rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Website
                        </label>
                        <Input
                          value={editData.website}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              website: e.target.value,
                            })
                          }
                          placeholder="e.g., https://company.com"
                          className="rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          How did you hear about us?
                        </label>
                        <Input
                          value={editData.howDidYouHear}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              howDidYouHear: e.target.value,
                            })
                          }
                          placeholder="e.g., Google search"
                          className="rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Specific Needs
                        </label>
                        <Input
                          value={editData.specificNeeds}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              specificNeeds: e.target.value,
                            })
                          }
                          placeholder="e.g., Employee wellness tracking"
                          className="rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200"
                        />
                      </div>
                      <div className="col-span-1 md:col-span-2 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-4">
                        <Button
                          variant="outline"
                          onClick={() => setIsEditing(false)}
                          className="px-4 py-2 rounded-xl border-gray-200 hover:bg-gray-50 text-sm"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSave}
                          disabled={isSaving}
                          className="px-4 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300 text-sm"
                        >
                          {isSaving ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            "Save Changes"
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                      {lead.companySize && (
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-gray-100 mt-1">
                            <Users className="h-4 w-4 text-gray-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-700">
                              Company Size
                            </p>
                            <p className="text-sm text-gray-600">
                              {lead.companySize}
                            </p>
                          </div>
                        </div>
                      )}
                      {lead.industry && (
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-gray-100 mt-1">
                            <Building className="h-4 w-4 text-gray-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-700">
                              Industry
                            </p>
                            <p className="text-sm text-gray-600">
                              {lead.industry}
                            </p>
                          </div>
                        </div>
                      )}
                      {lead.jobTitle && (
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-gray-100 mt-1">
                            <Briefcase className="h-4 w-4 text-gray-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-700">
                              Job Title
                            </p>
                            <p className="text-sm text-gray-600">
                              {lead.jobTitle}
                            </p>
                          </div>
                        </div>
                      )}
                      {lead.website && (
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-gray-100 mt-1">
                            <Globe className="h-4 w-4 text-gray-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-700">
                              Website
                            </p>
                            <p className="text-sm text-gray-600 break-words">
                              {lead.website}
                            </p>
                          </div>
                        </div>
                      )}
                      {lead.howDidYouHear && (
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-gray-100 mt-1">
                            <MessageSquare className="h-4 w-4 text-gray-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-700">
                              How did you hear about us?
                            </p>
                            <p className="text-sm text-gray-600">
                              {lead.howDidYouHear}
                            </p>
                          </div>
                        </div>
                      )}
                      {lead.specificNeeds && (
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-gray-100 mt-1">
                            <MessageSquare className="h-4 w-4 text-gray-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-700">
                              Specific Needs
                            </p>
                            <p className="text-sm text-gray-600">
                              {lead.specificNeeds}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                  {provisioningTrial ? (
                    <Button
                      disabled
                      className="h-10 sm:h-12 px-4 sm:px-6 rounded-xl bg-gray-100 text-gray-500 text-sm sm:text-base"
                    >
                      <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 mr-2 animate-spin" />
                      Starting Trial...
                    </Button>
                  ) : (trialData?.accountType === "trial" &&
                      trialData?.tenantId) ||
                    (lead.accountType === "trial" && lead.tenantId) ? (
                    <div className="flex items-center gap-3">
                      <Badge className="!bg-green-100 !text-green-800 !border-green-200 px-4 py-2 rounded-xl">
                        Trial Active
                      </Badge>
                      <Button
                        onClick={() =>
                          (window.location.href = `/${
                            trialData?.tenantId || lead.tenantId
                          }`)
                        }
                        className="h-10 sm:h-12 px-4 sm:px-6 rounded-xl bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-lg shadow-green-500/25 hover:shadow-green-500/40 transition-all duration-300 text-sm sm:text-base"
                      >
                        <Play className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                        View Trial
                      </Button>
                    </div>
                  ) : (
                    <Button
                      disabled={trialButtonDisabled}
                      onClick={handleProvisionTrial}
                      className="h-10 sm:h-12 px-4 sm:px-6 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300 text-sm sm:text-base"
                    >
                      <Play className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                      Start Trial
                    </Button>
                  )}
                </div>
              </div>

              {/* Notes Section */}
              {myNotes.docs && myNotes.docs.length > 0 && (
                <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-lg shadow-gray-200/30">
                  <h4 className="text-base md:text-lg font-bold text-gray-900 mb-4 md:mb-6 flex items-center gap-2 md:gap-3">
                    <div className="w-6 h-6 md:w-8 md:h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg md:rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                      <MessageSquare className="h-3 w-3 md:h-4 md:w-4 text-white" />
                    </div>
                    <span>Notes</span>
                    <div className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-xs font-semibold">
                      {myNotes.docs.length}
                    </div>
                  </h4>
                  <div className="space-y-4">
                    {myNotes.docs.map((n) => (
                      <NoteItem note={n} key={n._id} />
                    ))}
                  </div>
                </div>
              )}

              {/* Add Note Button */}
              <div className="flex justify-center">
                <Dialog open={noteModalOpen} onOpenChange={setNoteModalOpen}>
                  <DialogTrigger asChild>
                    <Button className="px-4 sm:px-6 py-2 sm:py-3 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300 text-sm sm:text-base">
                      <MessageSquare className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                      Add Note
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md bg-white/95 backdrop-blur-sm border-0 shadow-2xl shadow-gray-200/50 rounded-2xl mx-4">
                    <DialogHeader>
                      <DialogTitle className="text-lg sm:text-xl font-bold text-gray-900">
                        Add Note
                      </DialogTitle>
                    </DialogHeader>
                    {errorMessage && (
                      <div className="p-4 rounded-xl bg-red-50 border border-red-200">
                        <div className="text-red-800 font-medium">
                          {errorMessage}
                        </div>
                      </div>
                    )}
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Note
                        </label>
                        <Input
                          value={noteValue}
                          onChange={(e) => setNoteValue(e.target.value)}
                          placeholder="Enter your note..."
                          className="rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200"
                        />
                      </div>
                      <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                        <Button
                          variant="outline"
                          onClick={() => setNoteModalOpen(false)}
                          className="px-4 py-2 rounded-xl border-gray-200 hover:bg-gray-50 text-sm sm:text-base"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleAddNote}
                          disabled={!noteValue.trim()}
                          className="px-4 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300 text-sm sm:text-base"
                        >
                          Add Note
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default LeadItem;
